import React, { useState, useEffect, useMemo } from 'react';
import ReactFlow, { Background, Controls, ReactFlowProvider, getNodesBounds } from 'reactflow';
import 'reactflow/dist/style.css';
import CustomNode from './CustomNodeCircular';
import CustomEdge from './CustomEdge';
import _ from 'lodash';
import { createStructureOverview } from './createEdges';
import Legend from './Legend';
import { Col } from 'antd';
import WorkItemInsights from './workItemOverview';
import { formatDateToDDMMMYYYY } from '../../utils/function';

const nodeWidth = 50;
const nodeHeight = 50;

const nodeTypes = { customNode: CustomNode };
const edgeTypes = { customEdge: CustomEdge };


const occurrences = [
  { color: '#338BB0', label: 'First Pass' },
  { color: '#A8D8B9', label: 'Rework' },
];

const getLayoutedElements = (nodes, edges) => {
  let yPosition = 0;
  const xPosition = window.innerWidth / 2; // Centralize nodes horizontally

  nodes.forEach(node => {
    const sz = node.data.sz || 50; // Default size if not provided
    node.position = {
      x: xPosition - sz / 2,  // Centering the node horizontally
      y: yPosition,
    };
    node.style = {
      ...node.style,
      width: sz,
      height: sz,
      borderRadius: '50%' // Ensure nodes are circular
    };
    yPosition += sz + 25; // Increment y-position for the next node, add padding between nodes
  });

  return { nodes, edges };
};

const calTimeDifferenceInmilliSecond = (prevTimeStamp, curTimeStamp) => {
  if (!prevTimeStamp || !curTimeStamp)
    return 0;

  const sourcetimestamp_utc = new Date(prevTimeStamp);
  const targettimestamp_utc = new Date(curTimeStamp);
  const differenceInMs = targettimestamp_utc - sourcetimestamp_utc;

  return differenceInMs;
}

const edgeExists = (source, target, edges) => {
  const exist = edges.some(edge => edge.source === source && edge.target === target);
  return exist;
};

const convertTimeToMilliseconds = (timeStr) => {
  if (!timeStr) return 0;
  
  try {
    // Handle format like "20days 26:50:40"
    if (timeStr.includes('days')) {
      const [days, time] = timeStr.split('days');
      const [hours, minutes, seconds] = time.trim().split(':').map(Number);
      return (parseInt(days) * 24 * 60 * 60 * 1000) + 
             (hours * 60 * 60 * 1000) + 
             (minutes * 60 * 1000) + 
             (seconds * 1000);
    }
    
    // Handle format like "00:44:56"
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return (hours * 60 * 60 * 1000) + 
           (minutes * 60 * 1000) + 
           (seconds * 1000);
  } catch (error) {
    console.error('Error converting time:', timeStr, error);
    return 0;
  }
};

function ItemJourneyOverview({ apiUrl, itemId, apiData, stateGroups }) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [noDataMessage, setNoDataMessage] = useState('');
  const [translateExtent, setTranslateExtent] = useState([[0, 0], [Infinity, Infinity]]);

  const numberOfStateGroups = Object.keys(stateGroups).length;
  var timeSpentFirstOccurance = Array(numberOfStateGroups).fill(-1);
  var timeSpentRemainingOccurance = Array(numberOfStateGroups).fill(-1);

  const getStateCategoryIdByCategory = (category) => {
    const categories = Object.keys(stateGroups);

    for (let i = 0; i < categories.length; i++) {
      if (categories[i] === category) {
        return i; // Return the index of the found category
      }
    }

    console.log(`State Category ${category} not found`);
    return -1;
  };


  const processEdges = (apiData) => {
    const edges = [];
    var lastDateCategoryWise = new Map();
    var prevCategoryId = -1;
    var offsetReverseTransition = 0;
    let counter = 1;

    timeSpentFirstOccurance = Array(numberOfStateGroups).fill(-1);
    timeSpentRemainingOccurance = Array(numberOfStateGroups).fill(-1);

    const last = apiData.lifeCycle.length - 1;


    apiData.lifeCycle.forEach((item, index) => {
      var curCategoryId = getStateCategoryIdByCategory(item.work_item_state_category);

      if (curCategoryId >= 0) {
        if (prevCategoryId === -1) {
          prevCategoryId = curCategoryId;
          lastDateCategoryWise.set(curCategoryId, item.timestamp_utc);
        }
        else if (index === last || curCategoryId !== prevCategoryId) {
          if (timeSpentFirstOccurance[curCategoryId] < 0)
            timeSpentFirstOccurance[curCategoryId]++;
          if (timeSpentFirstOccurance[prevCategoryId] < 0)
            timeSpentFirstOccurance[prevCategoryId]++;
          const timeTaken = calTimeDifferenceInmilliSecond(lastDateCategoryWise.get(prevCategoryId), item.timestamp_utc);
          if (timeSpentFirstOccurance[prevCategoryId] === 0)
            timeSpentFirstOccurance[prevCategoryId] = timeTaken;
          else
            timeSpentRemainingOccurance[prevCategoryId] += timeTaken

          if (prevCategoryId < curCategoryId && !edgeExists(`structNode-${prevCategoryId}`, `structNode-${curCategoryId}`, edges)) {
            edges.push({
              id: `edge-structNode-${prevCategoryId}-structNode-${curCategoryId}`,
              source: `structNode-${prevCategoryId}`,
              target: `structNode-${curCategoryId}`,
              type: 'straight',
              labelStyle: { fontSize: '15px' },
              style: { stroke: 'black', strokeWidth: 0.5 },
              markerEnd: { type: 'arrowclosed', width: 40, height: 40 },
            });
          }
          else if (prevCategoryId > curCategoryId && !edgeExists(`structNode-${prevCategoryId}`, `structNode-${curCategoryId}`, edges)) {
            const isLeft = counter % 2 === 0;

            edges.push({
              id: `edge-structNode-${prevCategoryId}-structNode-${curCategoryId}`,
              source: `structNode-${prevCategoryId}`,
              target: `structNode-${curCategoryId}`,
              type: 'customEdge',
              sourceHandle: isLeft ? 'right' : 'left',
              targetHandle: isLeft ? 'left' : 'right',
              style: { stroke: 'red', strokewidth: 0.5, strokeDasharray: '5,5', markerEnd: 'url(#arrowhead)',  
                offsetReverseTransition: offsetReverseTransition ,
                side: isLeft ? 'left' : 'right'
              },
            });
            offsetReverseTransition += 8;
            counter++;
          }

          prevCategoryId = curCategoryId;
          lastDateCategoryWise.set(curCategoryId, item.timestamp_utc);
        }
      }
    });

    return edges;
  };

  useEffect(() => {
    if (!apiData || _.isEmpty(apiData.lifeCycle)) {
      setNoDataMessage(`No work item found with the given id ${itemId}`);
      setNodes([]);
      setEdges([]);
      return;
    }

    const categoryTimeMap = {};
    if (apiData.execution_time && Array.isArray(apiData.execution_time)) {
      apiData.execution_time.forEach(et => {
        if (et.state_category) {
          categoryTimeMap[et.state_category] = {
            firstPass: convertTimeToMilliseconds(et.first_pass_time),
            rework: convertTimeToMilliseconds(et.rework_time)
          };
        }
      });
    }

    const categoricalNodeList = Object.keys(stateGroups).map((category, index) => {
      const timeData = categoryTimeMap[category] || { firstPass: 0, rework: 0 };
      const totalTime = timeData.firstPass + timeData.rework;
      
      return {
        id: `structNode-${index}`,
        type: 'customNode',
        data: {
          state: category,
          timeSpentFirstOccurance: timeData.firstPass,
          timeSpentRemainingOccurance: timeData.rework,
          active: true,
          sz: totalTime > 0 ? 50 + Math.sqrt(totalTime) * 0.0035 : 50,
        },
        position: { x: 0, y: 0 },
        style: { width: nodeWidth, height: nodeHeight },
      };
    });

    const edgeList = processEdges(apiData);
    const structEdges = createStructureOverview(categoricalNodeList);
    const activeCategoricalNodeList = categoricalNodeList.filter(node => node.data.active === true);
    const allEdges = [...structEdges, ...edgeList];

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      activeCategoricalNodeList,
      allEdges
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setNoDataMessage('');
  }, [itemId, apiData, apiUrl]);

  useEffect(() => {
    if (nodes.length > 0) {
      const bounds = getNodesBounds(nodes);
      const paddingX = 1000;
      setTranslateExtent([
        [bounds.x - paddingX, bounds.y - 50],
        [bounds.width + bounds.x + paddingX, bounds.height + bounds.y + nodes[nodes.length - 1].style.width]
      ]);
    }
  }, [nodes]);

  const formatValue = (key, value) => {
    switch (key) {
      case 'created_date':
        return formatDateToDDMMMYYYY(value)
      case 'release_date':
        return formatDateToDDMMMYYYY(value);
        case 'lead_time':
        case 'lead_time *':
            return `${value}`;
          default:
            return value;
        }
      };

  const details = useMemo(() => {
    return Object.entries(apiData)
      .filter(([key]) => !['insights', 'lifeCycle', 'title', 'type', 'id', 'execution_time'].includes(key))
      .map(([key, value]) => {
        if (!value) return null;
        return (
          <li key={key} className="bg-[#f0f8ff] border border-[#e0f7fa] font-medium py-1 shadow-sm transition-all duration-300 ease-in-out overflow-hidden text-ellipsis max-w-full cursor-default mb-2.5 p-2 rounded flex gap-2.5 text-base">
            {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}: {formatValue(key, value)}
          </li>
        );
      })
      .filter(Boolean);
  }, [apiData]);

  const defaultViewport = { x: 0, y: 0, zoom: 0.8 };
  const proOptions = { hideAttribution: true };

  return (
    <div style={{ height: '87vh', width: '100%' }}>
      {noDataMessage !== '' ? (
        <div className="no-data-message text-3xl font-semibold mb-2 text-center" style={{ color: "#3f3f3", margin: '15% 0' }}>{noDataMessage}</div>
      ) : (
        <ReactFlowProvider>
          {apiData?.title && <Col span={12} className='flex flex-col absolute top-[16%] w-[290px] left-20 '>
            <div className="summary-card">
              <div className=' outline-[transparent] rounded-sm text-base text-[rgba(0,90,158,1)] '>{apiData?.type} {apiData?.id}</div>
              <div className="card-header"  style={{ marginLeft: '-7px' }}>
                <div className="card-title mb-2">{apiData?.title}</div>
                <div className="card-body w-full">
                  <ul>
                    {details}
                  </ul>
                </div>
              </div>
            </div>
          </Col>}
          <ReactFlow
            translateExtent={translateExtent}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            nodesDraggable={false}
            style={{ height: '100%', width: '100%' }}
            zoomOnScroll={false}
            panOnScroll={true}
            panOnDrag={true}
            defaultViewport={defaultViewport}
            fitView
            proOptions={proOptions}
          >
            <Background color='#ffffff' />
            <Controls />

          </ReactFlow>
          <Legend
            occurrences={occurrences}
            style={{
              position: 'absolute',
              top: -8,
              right: "19%",
              zIndex: 10
            }}
          />
        </ReactFlowProvider>
      )}
      {(noDataMessage === '' && apiData?.insights?.length) ? <Col span={12} className='flex flex-col absolute top-[12%] w-96 right-2 '>
        <div className="summary-card">
          <div className="card-header ">
            <div className="card-title mb-2">Insights</div>
            <WorkItemInsights item={apiData} />
          </div>
        </div>
      </Col>
      :""
      }
    </div>
  );
}

export default ItemJourneyOverview;
